import type { CurvaDocument } from "@/document/model/document";
import { findSceneNode, getAllSceneNodes } from "@/document/model/document";
import type { DocumentStyle, DocumentStyleKind } from "@/document/model/documentStyles";
import {
  applyStyleBinding,
  createStyleFromSelection,
  findDocumentStyle,
  inlineStyleBinding,
  normalizeStyleLibrary,
  styleBindingId,
  type CreateStyleFromSelectionOptions,
} from "@/document/model/documentStyles";
import type { SceneNode } from "@/document/model/nodes";
import type { Command, CommandContext, CommandResult } from "./Command";
import { finalizeDocumentGeometry } from "./nodeCommands";

function requireNode(document: CurvaDocument, nodeId: string): SceneNode {
  const node = findSceneNode(document, nodeId);
  if (!node) {
    throw new Error(`Node ${nodeId} was not found.`);
  }
  return node;
}

function replaceNode(document: CurvaDocument, nodeId: string, nextNode: SceneNode): void {
  const node = requireNode(document, nodeId);
  Object.assign(node, structuredClone(nextNode));
}

export class CreateDocumentStyleCommand implements Command {
  readonly id = "style.create";
  readonly label = "Create Style";

  private createdStyle?: DocumentStyle;
  private beforeBindings?: SceneNode["styleBindings"];

  constructor(
    private readonly nodeId: string,
    private readonly kind: DocumentStyleKind,
    private readonly name: string,
    private readonly options: CreateStyleFromSelectionOptions = {},
  ) {}

  execute(context: CommandContext): CommandResult {
    if (this.createdStyle) {
      const library = normalizeStyleLibrary(context.document.styles);
      if (!library.items.some(style => style.id === this.createdStyle!.id)) {
        library.items.push(structuredClone(this.createdStyle));
        context.document.styles = library;
      }
      const node = requireNode(context.document, this.nodeId);
      const nextNode = applyStyleBinding(node, this.createdStyle);
      replaceNode(context.document, this.nodeId, nextNode);
      finalizeDocumentGeometry(context.document, [this.nodeId]);
      return {
        changedNodeIds: [this.nodeId],
        affectedBounds: [node.bounds],
      };
    }

    const node = requireNode(context.document, this.nodeId);
    const style = createStyleFromSelection(node, this.kind, this.name, this.options);
    const library = normalizeStyleLibrary(context.document.styles);
    library.items.push(style);
    context.document.styles = library;
    this.createdStyle = style;
    this.beforeBindings = structuredClone(node.styleBindings);
    const nextNode = applyStyleBinding(node, style);
    replaceNode(context.document, this.nodeId, nextNode);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.createdStyle) {
      throw new Error("Cannot undo style creation; style snapshot is missing.");
    }
    const node = requireNode(context.document, this.nodeId);
    node.styleBindings = this.beforeBindings ? structuredClone(this.beforeBindings) : undefined;
    finalizeDocumentGeometry(context.document, [this.nodeId]);

    const library = normalizeStyleLibrary(context.document.styles);
    library.items = library.items.filter(style => style.id !== this.createdStyle!.id);
    context.document.styles = library;
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  get createdStyleId(): string | undefined {
    return this.createdStyle?.id;
  }
}

export class ApplyDocumentStyleCommand implements Command {
  readonly id = "style.apply";
  readonly label = "Apply Style";

  private beforeBindings?: SceneNode["styleBindings"];

  constructor(
    private readonly nodeId: string,
    private readonly styleId: string,
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireNode(context.document, this.nodeId);
    const style = findDocumentStyle(context.document, this.styleId);
    if (!style) {
      throw new Error(`Style ${this.styleId} was not found.`);
    }

    this.beforeBindings = structuredClone(node.styleBindings);
    const nextNode = applyStyleBinding(node, style);
    replaceNode(context.document, this.nodeId, nextNode);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    const node = requireNode(context.document, this.nodeId);
    node.styleBindings = this.beforeBindings ? structuredClone(this.beforeBindings) : undefined;
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class DetachDocumentStyleCommand implements Command {
  readonly id = "style.detach";
  readonly label = "Detach Style";

  private before?: {
    style: SceneNode["style"];
    text?: Extract<SceneNode, { type: "text" }>["text"];
    styleBindings?: SceneNode["styleBindings"];
  };

  constructor(
    private readonly nodeId: string,
    private readonly kind: DocumentStyleKind,
  ) {}

  execute(context: CommandContext): CommandResult {
    const node = requireNode(context.document, this.nodeId);
    this.before = {
      style: structuredClone(node.style),
      text: node.type === "text" ? structuredClone(node.text) : undefined,
      styleBindings: structuredClone(node.styleBindings),
    };

    const nextNode = inlineStyleBinding(context.document, node, this.kind);
    replaceNode(context.document, this.nodeId, nextNode);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.before) {
      throw new Error("Cannot undo detach style; snapshot is missing.");
    }

    const node = requireNode(context.document, this.nodeId);
    node.style = structuredClone(this.before.style);
    if (node.type === "text" && this.before.text) {
      node.text = structuredClone(this.before.text);
    }
    node.styleBindings = this.before.styleBindings ? structuredClone(this.before.styleBindings) : undefined;
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class UpdateDocumentStyleCommand implements Command {
  readonly id = "style.update";
  readonly label = "Update Style";

  private before?: DocumentStyle;

  constructor(
    private readonly styleId: string,
    private readonly nextStyle: DocumentStyle,
  ) {}

  execute(context: CommandContext): CommandResult {
    const library = normalizeStyleLibrary(context.document.styles);
    const index = library.items.findIndex(style => style.id === this.styleId);
    if (index < 0) {
      throw new Error(`Style ${this.styleId} was not found.`);
    }

    if (!this.before) {
      this.before = structuredClone(library.items[index]);
    }

    library.items[index] = structuredClone(this.nextStyle);
    context.document.styles = library;

    const changedNodeIds = getAllSceneNodes(context.document)
      .filter(node => Object.values(node.styleBindings ?? {}).includes(this.styleId))
      .map(node => node.id);
    if (changedNodeIds.length > 0) {
      finalizeDocumentGeometry(context.document, changedNodeIds);
    }

    return {
      changedNodeIds,
      affectedBounds: changedNodeIds
        .map(nodeId => findSceneNode(context.document, nodeId)?.bounds)
        .filter((bounds): bounds is NonNullable<typeof bounds> => bounds !== undefined),
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.before) {
      throw new Error("Cannot undo style update; style snapshot is missing.");
    }

    const library = normalizeStyleLibrary(context.document.styles);
    const index = library.items.findIndex(style => style.id === this.styleId);
    if (index < 0) {
      throw new Error(`Style ${this.styleId} was not found.`);
    }

    library.items[index] = structuredClone(this.before);
    context.document.styles = library;

    const changedNodeIds = getAllSceneNodes(context.document)
      .filter(node => Object.values(node.styleBindings ?? {}).includes(this.styleId))
      .map(node => node.id);
    if (changedNodeIds.length > 0) {
      finalizeDocumentGeometry(context.document, changedNodeIds);
    }

    return {
      changedNodeIds,
      affectedBounds: changedNodeIds
        .map(nodeId => findSceneNode(context.document, nodeId)?.bounds)
        .filter((bounds): bounds is NonNullable<typeof bounds> => bounds !== undefined),
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class DeleteDocumentStyleCommand implements Command {
  readonly id = "style.delete";
  readonly label = "Delete Style";

  private beforeStyle?: DocumentStyle;
  private beforeIndex?: number;
  private beforeNodes?: Array<{
    nodeId: string;
    style: SceneNode["style"];
    text?: Extract<SceneNode, { type: "text" }>["text"];
    styleBindings?: SceneNode["styleBindings"];
  }>;

  constructor(private readonly styleId: string) {}

  execute(context: CommandContext): CommandResult {
    const style = findDocumentStyle(context.document, this.styleId);
    if (!style) {
      throw new Error(`Style ${this.styleId} was not found.`);
    }

    const nodes = getAllSceneNodes(context.document)
      .filter(node => styleBindingId(node, style.kind) === this.styleId);

    if (!this.beforeStyle) {
      this.beforeStyle = structuredClone(style);
      this.beforeIndex = normalizeStyleLibrary(context.document.styles)
        .items.findIndex(candidate => candidate.id === style.id);
      this.beforeNodes = nodes.map(node => ({
        nodeId: node.id,
        style: structuredClone(node.style),
        text: node.type === "text" ? structuredClone(node.text) : undefined,
        styleBindings: structuredClone(node.styleBindings),
      }));
    }

    for (const node of nodes) {
      const nextNode = inlineStyleBinding(context.document, node, style.kind);
      replaceNode(context.document, node.id, nextNode);
    }

    const library = normalizeStyleLibrary(context.document.styles);
    library.items = library.items.filter(candidate => candidate.id !== this.styleId);
    context.document.styles = library;

    const changedNodeIds = nodes.map(node => node.id);
    if (changedNodeIds.length > 0) {
      finalizeDocumentGeometry(context.document, changedNodeIds);
    }

    return {
      changedNodeIds,
      affectedBounds: nodes.map(node => node.bounds),
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeStyle || !this.beforeNodes) {
      throw new Error("Cannot undo style deletion; style snapshot is missing.");
    }

    const library = normalizeStyleLibrary(context.document.styles);
    if (!library.items.some(style => style.id === this.beforeStyle!.id)) {
      const insertIndex = Math.max(0, Math.min(this.beforeIndex ?? library.items.length, library.items.length));
      library.items.splice(insertIndex, 0, structuredClone(this.beforeStyle));
      context.document.styles = library;
    }

    for (const snapshot of this.beforeNodes) {
      const node = requireNode(context.document, snapshot.nodeId);
      node.style = structuredClone(snapshot.style);
      if (node.type === "text" && snapshot.text) {
        node.text = structuredClone(snapshot.text);
      }
      node.styleBindings = snapshot.styleBindings ? structuredClone(snapshot.styleBindings) : undefined;
    }

    const changedNodeIds = this.beforeNodes.map(snapshot => snapshot.nodeId);
    if (changedNodeIds.length > 0) {
      finalizeDocumentGeometry(context.document, changedNodeIds);
    }

    return {
      changedNodeIds,
      affectedBounds: changedNodeIds
        .map(nodeId => findSceneNode(context.document, nodeId)?.bounds)
        .filter((bounds): bounds is NonNullable<typeof bounds> => bounds !== undefined),
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}
